import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { PencilIcon, PlusIcon, XMarkIcon, MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { GET_DISCOUNT_RULE, GET_DISCOUNT_CODES } from '../../graphql/discount-queries'
import { GET_CATEGORIES, GET_PRODUCT_VARIANTS } from '../../graphql/product-queries'
import {
  DEACTIVATE_DISCOUNT_RULE,
  UPDATE_DISCOUNT_RULE,
  ADD_DISCOUNT_CODE,
  UPDATE_DISCOUNT_CODE,
  DEACTIVATE_DISCOUNT_CODE,
} from '../../graphql/discount-mutations'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import LoadingSpinner from '../../components/LoadingSpinner'
import type { DiscountRule, DiscountCondition, DiscountCode } from '../../types/discount'

type Tab = 'details' | 'codes'
type ActiveFilter = 'all' | 'active' | 'inactive'

// Omit ambiguous characters (0, O, I, 1) for readability
const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
function generateCode(): string {
  return Array.from({ length: 8 }, () => CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]).join('')
}

// Local on purpose, not lib/format.ts: DiscountRule / DiscountCode timestamps are still
// epoch milliseconds on the API (its todo, audit 2026-10-08) — the Order fields went ISO.
function parseApiDate(val: string | undefined | null): Date | null {
  if (!val) return null
  const num = Number(val)
  const date = isNaN(num) ? new Date(val) : new Date(num)
  return isNaN(date.getTime()) ? null : date
}

function localDateTimeString(date: Date): string {
  // Offset to local time so .toISOString().slice(0,16) gives the local datetime string
  // that <input type="datetime-local"> expects (not UTC)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

function expiryFromNow(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return localDateTimeString(d)
}

function formatEffect(rule: DiscountRule): string {
  if (rule.effect_type === 'PERCENTAGE') return `${rule.effect_value / 100}%`
  if (rule.effect_type === 'MULTI_BUY') {
    return `Any ${rule.multi_buy_qty ?? '?'} for €${(rule.effect_value / 100).toFixed(2)}`
  }
  return `€${(rule.effect_value / 100).toFixed(2)}`
}

function parseConditionValue(value_json: unknown): unknown {
  if (typeof value_json === 'string') {
    try { return JSON.parse(value_json) } catch { return value_json }
  }
  return value_json
}

function formatConditionValue(c: DiscountCondition, variantMap: Record<number, string> = {}): string {
  const val = parseConditionValue(c.value_json)
  if (c.type === 'MIN_ORDER_VALUE') return `€${((val as number) / 100).toFixed(2)}`
  if (c.type === 'DAYS_OF_WEEK') {
    const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    return (val as number[]).map((d) => names[d - 1]).join(', ')
  }
  if (c.type === 'REQUIRED_ITEMS' && Array.isArray(val)) {
    return (val as number[]).map((id) =>
      variantMap[id] ? `${variantMap[id]} (#${id})` : `#${id}`
    ).join(', ')
  }
  if (Array.isArray(val)) return val.join(', ')
  return String(val)
}

const CONDITION_LABELS: Record<string, string> = {
  MIN_ORDER_VALUE: 'Min order value',
  MIN_QUANTITY: 'Min quantity',
  REQUIRED_ITEMS: 'Required items',
  DAYS_OF_WEEK: 'Days of week',
  TIME_FROM: 'Time from',
  TIME_TO: 'Time to',
  ORDER_TYPE: 'Order type',
}

const BACK_HREF: Record<string, string> = {
  AUTOMATIC: '/discounts/automatic',
  CODE: '/discounts/coupon',
  STAFF_APPLIED: '/discounts/staff',
}

const EXPIRY_DAYS = [7, 30, 60, 90]

// ── Expiry quick-pick ─────────────────────────────────────────────────────────
function ExpiryPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex gap-1.5 flex-wrap">
        {EXPIRY_DAYS.map((d) => {
          const v = expiryFromNow(d)
          return (
            <button
              key={d}
              type="button"
              onClick={() => onChange(v)}
              className={`px-2 py-1 rounded text-xs border transition-all ${
                value === v
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              +{d}d
            </button>
          )
        })}
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="px-2 py-1 rounded text-xs border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200"
          >
            Clear
          </button>
        )}
      </div>
      <input
        type="datetime-local"
        className="input text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

export default function DiscountRuleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [activeTab, setActiveTab] = useState<Tab>('details')

  // Add form state
  const [showAddCode, setShowAddCode] = useState(false)
  const [newCode, setNewCode] = useState('')
  const [newCodeDescription, setNewCodeDescription] = useState('')
  const [newCodeMaxUses, setNewCodeMaxUses] = useState('')
  const [newCodeExpiresAt, setNewCodeExpiresAt] = useState('')

  // Filter state
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')

  // Edit state
  const [editingCodeId, setEditingCodeId] = useState<number | null>(null)
  const [editDescription, setEditDescription] = useState('')
  const [editMaxUses, setEditMaxUses] = useState('')
  const [editExpiresAt, setEditExpiresAt] = useState('')

  // Confirm deactivate state
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<number | null>(null)

  const { data, loading, error, refetch } = useQuery(GET_DISCOUNT_RULE, {
    variables: { id: parseInt(id!, 10) },
  })
  const rule: DiscountRule | undefined = data?.discountRule

  const { data: categoriesData } = useQuery(GET_CATEGORIES, {
    skip: rule?.scope_type !== 'CATEGORY' || !rule?.scope_value_json?.length,
  })
  const categoryMap: Record<number, string> = {}
  for (const cat of categoriesData?.categories ?? []) {
    categoryMap[cat.id] = cat.name
  }

  const requiredItemIds = rule?.conditions
    .filter((c) => c.type === 'REQUIRED_ITEMS')
    .flatMap((c) => {
      const val = parseConditionValue(c.value_json)
      return Array.isArray(val) ? (val as number[]) : []
    }) ?? []
  const scopeItemIds = rule?.scope_type === 'ITEM' ? (rule.scope_value_json ?? []) : []
  const allVariantIds = [...new Set([...requiredItemIds, ...scopeItemIds])]
  const { data: variantsData } = useQuery(GET_PRODUCT_VARIANTS, {
    variables: { filters: { ids: allVariantIds } },
    skip: allVariantIds.length === 0,
  })
  const variantMap: Record<number, string> = {}
  for (const v of variantsData?.productVariants ?? []) {
    variantMap[v.id] = v.product?.name ? `${v.product.name} – ${v.name}` : v.name
  }

  const activeVar = activeFilter === 'all' ? undefined : activeFilter === 'active'
  const {
    data: codesData,
    loading: codesLoading,
    refetch: refetchCodes,
  } = useQuery(GET_DISCOUNT_CODES, {
    variables: {
      rule_id: parseInt(id!, 10),
      active: activeVar,
      search: search.trim() || undefined,
    },
    skip: activeTab !== 'codes' || rule?.trigger_type !== 'CODE',
  })
  const codes: DiscountCode[] = codesData?.discountCodes ?? []

  const [deactivateRule, { loading: deactivating }] = useMutation(DEACTIVATE_DISCOUNT_RULE, {
    onCompleted() { toast.success('Rule deactivated'); refetch() },
    onError(err) { toast.error(err.message) },
  })

  const [activateRule, { loading: activating }] = useMutation(UPDATE_DISCOUNT_RULE, {
    onCompleted() { toast.success('Rule activated'); refetch() },
    onError(err) { toast.error(err.message) },
  })

  const [addCode, { loading: addingCode }] = useMutation(ADD_DISCOUNT_CODE, {
    onCompleted() {
      toast.success('Code added')
      setShowAddCode(false)
      setNewCode('')
      setNewCodeDescription('')
      setNewCodeMaxUses('')
      setNewCodeExpiresAt('')
      refetchCodes()
    },
    onError(err) { toast.error(err.message) },
  })

  const [updateCode, { loading: updatingCode }] = useMutation(UPDATE_DISCOUNT_CODE, {
    onCompleted() {
      toast.success('Code updated')
      setEditingCodeId(null)
      refetchCodes()
    },
    onError(err) { toast.error(err.message) },
  })

  const [deactivateCode] = useMutation(DEACTIVATE_DISCOUNT_CODE, {
    onCompleted() {
      toast.success('Code deactivated')
      setConfirmDeactivateId(null)
      refetchCodes()
    },
    onError(err) { toast.error(err.message) },
  })

  function handleAddCode(e: React.FormEvent) {
    e.preventDefault()
    if (!newCode.trim()) return toast.error('Code is required')
    const vars: Record<string, unknown> = { rule_id: rule!.id, code: newCode.trim().toUpperCase() }
    if (newCodeDescription.trim()) vars.description = newCodeDescription.trim()
    if (newCodeMaxUses) vars.max_uses = parseInt(newCodeMaxUses, 10)
    if (newCodeExpiresAt) vars.expires_at = new Date(newCodeExpiresAt).toISOString()
    addCode({ variables: vars })
  }

  function startEdit(code: DiscountCode) {
    setEditingCodeId(code.id)
    setEditDescription(code.description ?? '')
    setEditMaxUses(code.max_uses != null ? String(code.max_uses) : '')
    setEditExpiresAt(code.expires_at ? localDateTimeString(parseApiDate(code.expires_at) ?? new Date()) : '')
    setConfirmDeactivateId(null)
  }

  function handleUpdateCode(e: React.FormEvent) {
    e.preventDefault()
    const vars: Record<string, unknown> = {
      id: editingCodeId,
      description: editDescription.trim() || null,
      max_uses: editMaxUses ? parseInt(editMaxUses, 10) : null,
      expires_at: editExpiresAt ? new Date(editExpiresAt).toISOString() : null,
    }
    updateCode({ variables: vars })
  }

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  if (error || !rule) {
    return (
      <div className="card p-8 text-center text-red-600">
        Failed to load discount rule.{' '}
        <button className="underline" onClick={() => navigate('/discounts')}>Go back</button>
      </div>
    )
  }

  const backHref = BACK_HREF[rule.trigger_type] ?? '/discounts'
  const canManage = hasPermission(PERMISSIONS.DISCOUNT_MANAGE)
  const colSpan = canManage ? 7 : 6

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Link to={backHref} className="text-sm text-gray-500 hover:text-gray-700">← Back</Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-2xl font-bold text-gray-900">{rule.name}</h1>
          {rule.active ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>
          )}
        </div>
        <div className="flex gap-2">
          {canManage && (
            <Link to={`/discounts/${rule.id}/edit`} className="btn-secondary flex items-center gap-1">
              <PencilIcon className="h-4 w-4" />
              Edit
            </Link>
          )}
          {canManage && (
            rule.active ? (
              <button className="btn-danger" disabled={deactivating}
                onClick={() => deactivateRule({ variables: { id: rule.id } })}>
                {deactivating ? 'Deactivating…' : 'Deactivate'}
              </button>
            ) : (
              <button className="btn-secondary" disabled={activating}
                onClick={() => activateRule({ variables: { id: rule.id, input: { active: true } } })}>
                {activating ? 'Activating…' : 'Activate'}
              </button>
            )
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Details
          </button>
          {rule.trigger_type === 'CODE' && (
            <button
              type="button"
              onClick={() => setActiveTab('codes')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'codes'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Codes
              {rule.codes.length > 0 && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {rule.codes.length}
                </span>
              )}
            </button>
          )}
        </nav>
      </div>

      {/* ── Details tab ── */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="card p-6">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Rule details</h2>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><dt className="text-gray-500">Class</dt><dd className="font-medium text-gray-900">{rule.discount_class}</dd></div>
                <div><dt className="text-gray-500">Effect</dt><dd className="font-medium text-gray-900">{formatEffect(rule)} ({rule.effect_type})</dd></div>
                <div><dt className="text-gray-500">Scope</dt><dd className="font-medium text-gray-900">{rule.scope_type}</dd></div>
                {rule.scope_value_json && rule.scope_value_json.length > 0 && (
                  <div>
                    <dt className="text-gray-500">
                      {rule.scope_type === 'CATEGORY' ? 'Categories' : rule.scope_type === 'ITEM' ? 'Variants' : 'Scope IDs'}
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {rule.scope_type === 'CATEGORY'
                        ? rule.scope_value_json.map((scopeId) =>
                            categoryMap[scopeId]
                              ? `${categoryMap[scopeId]} (#${scopeId})`
                              : `#${scopeId}`
                          ).join(', ')
                        : rule.scope_type === 'ITEM'
                        ? rule.scope_value_json.map((scopeId) =>
                            variantMap[scopeId]
                              ? `${variantMap[scopeId]} (#${scopeId})`
                              : `#${scopeId}`
                          ).join(', ')
                        : rule.scope_value_json.join(', ')}
                    </dd>
                  </div>
                )}
                <div><dt className="text-gray-500">Allocation</dt><dd className="font-medium text-gray-900">{rule.allocation}</dd></div>
                <div><dt className="text-gray-500">Trigger</dt><dd className="font-medium text-gray-900">{rule.trigger_type}</dd></div>
                {rule.min_staff_role && (
                  <div><dt className="text-gray-500">Min staff role</dt><dd className="font-medium text-gray-900">{rule.min_staff_role}</dd></div>
                )}
                <div><dt className="text-gray-500">Priority</dt><dd className="font-medium text-gray-900">{rule.priority}</dd></div>
              </dl>
            </div>

            <div className="card p-6">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Conditions</h2>
              {rule.conditions.length === 0 ? (
                <p className="text-sm text-gray-400 italic">None — applies unconditionally.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-2 w-1/3">Type</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-2">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rule.conditions.map((c, i) => (
                      <tr key={i}>
                        <td className="py-2.5 pr-4 text-gray-500 whitespace-nowrap">{CONDITION_LABELS[c.type] ?? c.type}</td>
                        <td className="py-2.5 font-medium text-gray-900">{formatConditionValue(c, variantMap)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {rule.limit && (
            <div className="lg:col-span-1">
              <div className="card p-6">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Limit</h2>
                <dl className="space-y-3 text-sm">
                  {rule.limit.max_uses_total != null && (
                    <div><dt className="text-gray-500">Max total uses</dt><dd className="font-medium text-gray-900">{rule.limit.max_uses_total}</dd></div>
                  )}
                  {rule.limit.starts_at && (
                    <div><dt className="text-gray-500">Starts at</dt><dd className="font-medium text-gray-900">{parseApiDate(rule.limit.starts_at)?.toLocaleString() ?? '—'}</dd></div>
                  )}
                  {rule.limit.ends_at && (
                    <div><dt className="text-gray-500">Ends at</dt><dd className="font-medium text-gray-900">{parseApiDate(rule.limit.ends_at)?.toLocaleString() ?? '—'}</dd></div>
                  )}
                </dl>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Codes tab ── */}
      {activeTab === 'codes' && rule.trigger_type === 'CODE' && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                className="input pl-9 py-1.5 text-sm w-full"
                placeholder="Search codes…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex rounded-md shadow-sm">
              {(['all', 'active', 'inactive'] as ActiveFilter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setActiveFilter(f)}
                  className={`px-3 py-1.5 text-sm border first:rounded-l-md last:rounded-r-md capitalize ${
                    activeFilter === f
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            {!codesLoading && (
              <span className="text-xs text-gray-400">
                {codes.length} code{codes.length !== 1 ? 's' : ''}
              </span>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => { setShowAddCode((v) => !v); setEditingCodeId(null) }}
                className="btn-primary flex items-center gap-1.5 text-sm py-1.5 ml-auto"
              >
                <PlusIcon className="h-4 w-4" />
                Add code
              </button>
            )}
          </div>

          {/* Add form */}
          {showAddCode && (
            <form
              onSubmit={handleAddCode}
              className="p-4 bg-indigo-50 rounded-xl border border-indigo-100 space-y-4"
            >
              <div className="flex flex-wrap gap-4 items-start">
                {/* Code + generate */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
                  <div className="flex gap-1.5">
                    <input
                      autoFocus
                      className="input uppercase font-mono tracking-widest w-36"
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value)}
                      placeholder="e.g. CURRY10"
                    />
                    <button
                      type="button"
                      onClick={() => setNewCode(generateCode())}
                      className="btn-secondary text-xs px-2 flex items-center gap-1"
                      title="Generate random code"
                    >
                      <ArrowPathIcon className="h-3.5 w-3.5" />
                      Generate
                    </button>
                  </div>
                </div>

                {/* Description */}
                <div className="flex-1 min-w-48">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <input
                    className="input w-full"
                    value={newCodeDescription}
                    onChange={(e) => setNewCodeDescription(e.target.value)}
                    placeholder="e.g. Summer promo for influencers"
                  />
                </div>

                {/* Max uses */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Max uses <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="input w-28"
                    value={newCodeMaxUses}
                    onChange={(e) => setNewCodeMaxUses(e.target.value)}
                    placeholder="Unlimited"
                  />
                </div>
              </div>

              {/* Expiry */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Expires at <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <ExpiryPicker value={newCodeExpiresAt} onChange={setNewCodeExpiresAt} />
              </div>

              <div className="flex items-center gap-2">
                <button type="submit" className="btn-primary text-sm py-2" disabled={addingCode}>
                  {addingCode ? 'Saving…' : 'Save code'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddCode(false)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </form>
          )}

          {/* Codes table */}
          {codesLoading && <div className="flex justify-center py-12"><LoadingSpinner /></div>}
          {!codesLoading && codes.length === 0 && (
            <div className="card p-12 text-center">
              <p className="text-sm text-gray-500">No codes match your filter.</p>
            </div>
          )}
          {!codesLoading && codes.length > 0 && (
            <div className="card overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max uses</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    {canManage && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {codes.map((code) => (
                    <>
                      {/* ── Data row ── */}
                      <tr key={code.id} className={code.active ? '' : 'opacity-60'}>
                        <td className="px-4 py-3 font-mono text-sm font-semibold text-gray-900">{code.code}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {code.description ?? <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {code.active ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{code.max_uses ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {code.expires_at ? (parseApiDate(code.expires_at)?.toLocaleDateString() ?? '—') : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {parseApiDate(code.created_at)?.toLocaleDateString() ?? '—'}
                        </td>
                        {canManage && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {/* Edit button */}
                            {editingCodeId !== code.id && confirmDeactivateId !== code.id && (
                              <button
                                type="button"
                                onClick={() => startEdit(code)}
                                className="text-xs text-indigo-500 hover:text-indigo-700 mr-3"
                              >
                                Edit
                              </button>
                            )}

                            {/* Activate / deactivate flow */}
                            {confirmDeactivateId === code.id ? (
                              <span className="inline-flex items-center gap-2">
                                <span className="text-xs text-gray-500">Are you sure?</span>
                                <button
                                  type="button"
                                  onClick={() => deactivateCode({ variables: { id: code.id } })}
                                  className="text-xs text-red-600 font-semibold hover:text-red-800"
                                >
                                  Deactivate
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeactivateId(null)}
                                  className="text-xs text-gray-400 hover:text-gray-600"
                                >
                                  Cancel
                                </button>
                              </span>
                            ) : editingCodeId !== code.id ? (
                              code.active ? (
                                <button
                                  type="button"
                                  onClick={() => { setConfirmDeactivateId(code.id); setEditingCodeId(null) }}
                                  className="text-xs text-red-400 hover:text-red-600"
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => updateCode({ variables: { id: code.id, active: true } })}
                                  className="text-xs text-green-600 hover:text-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                  disabled={updatingCode}
                                >
                                  Activate
                                </button>
                              )
                            ) : null}
                          </td>
                        )}
                      </tr>

                      {/* ── Edit row (inline, spans full width) ── */}
                      {editingCodeId === code.id && (
                        <tr key={`edit-${code.id}`}>
                          <td colSpan={colSpan} className="px-4 py-4 bg-amber-50 border-t border-amber-100">
                            <form onSubmit={handleUpdateCode} className="space-y-3">
                              <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
                                Editing {code.code}
                              </p>
                              <div className="flex flex-wrap gap-4 items-start">
                                <div className="flex-1 min-w-48">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Description <span className="font-normal text-gray-400">(optional)</span>
                                  </label>
                                  <input
                                    autoFocus
                                    className="input w-full"
                                    value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                    placeholder="e.g. Summer promo for influencers"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Max uses <span className="font-normal text-gray-400">(optional)</span>
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    className="input w-28"
                                    value={editMaxUses}
                                    onChange={(e) => setEditMaxUses(e.target.value)}
                                    placeholder="Unlimited"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Expires at <span className="font-normal text-gray-400">(optional)</span>
                                </label>
                                <ExpiryPicker value={editExpiresAt} onChange={setEditExpiresAt} />
                              </div>
                              <div className="flex items-center gap-2">
                                <button type="submit" className="btn-primary text-sm py-1.5" disabled={updatingCode}>
                                  {updatingCode ? 'Saving…' : 'Save changes'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingCodeId(null)}
                                  className="btn-secondary text-sm py-1.5"
                                >
                                  Cancel
                                </button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
